<template>
  <div class="header">
    <span class="text-subtle">Ansökningsnummer: {{ depositLoan.application.publicNumber }}</span>
    <div class="header-row">
      <div>
        <h1 class="title">{{ collateralLabel }}</h1>
        <p class="status">
          Status: <span class="status-value">{{ statusLabel }}</span>
        </p>
      </div>

      <AppRouterLink :to="`/ansokningar/${depositLoan.application.id}`" variant="button" class="application-link">
        Till ansökan
        <SvgIcon :path="mdiFileDocumentOutline" size="16" />
      </AppRouterLink>
    </div>

    <div class="applicants">
      <RouterLink
        v-for="applicant in depositLoan.applicants"
        :key="applicant.userId"
        :to="`/kunder/${applicant.userId}`"
        class="applicant-box"
      >
        <span class="applicant-role">Låntagare</span>

        <div class="applicant-identity">
          <div class="applicant-name">{{ applicant.givenName }} {{ applicant.surname }}</div>
          <div class="text-subtle">{{ applicant.personalNumber }}</div>
        </div>

        <div class="applicant-contact">
          <span v-if="applicant.contactDetails.phoneNumber" class="contact-line">
            <SvgIcon :path="mdiPhoneOutline" size="16" class="contact-icon" />
            <span class="contact-value">{{ applicant.contactDetails.phoneNumber }}</span>
          </span>
          <span v-if="applicant.contactDetails.email" class="contact-line">
            <SvgIcon :path="mdiEmailOutline" size="16" class="contact-icon" />
            <span class="contact-value" :title="applicant.contactDetails.email">
              {{ applicant.contactDetails.email }}
            </span>
          </span>
          <span v-if="applicant.address" class="contact-line">
            <SvgIcon :path="mdiMapMarkerOutline" size="16" class="contact-icon" />
            <span class="contact-value" :title="applicant.address">{{ applicant.address }}</span>
          </span>
        </div>
      </RouterLink>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { mdiEmailOutline, mdiFileDocumentOutline, mdiMapMarkerOutline, mdiPhoneOutline } from '@mdi/js'
import { SvgIcon } from '@staff/ui'
import AppRouterLink from '@/components/AppRouterLink.vue'
import type { DepositLoan } from '@/services/depositLoan.types'

const { t } = useI18n()

const props = defineProps<{ depositLoan: DepositLoan }>()

const collateralLabel = computed(() => {
  const property = props.depositLoan.collateralProperty
  if (!property) return '–'
  const type = t(`PROPERTY_TYPE.${property.type}`)
  const address = property.streetNumber ? `${property.street} ${property.streetNumber}` : property.street
  return `${type}, ${address}`
})

const statusLabel = computed(() => (props.depositLoan.endDate === null ? 'Aktivt' : 'Avslutat'))
</script>

<style scoped lang="scss">
.header {
  padding: var(--space-16);
  border: 1px solid var(--color-border-default);
  border-radius: var(--radius-lg);
  background-color: var(--color-canvas-subtle);
}

.header-row {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--space-8);
  margin-bottom: var(--space-12);
}

// AppRouterLink's button variant supplies the shape; the colour is the caller's,
// matching AppButton's blue so the two read as the same control.
.application-link {
  display: inline-flex;
  align-items: center;
  gap: var(--space-6);
  background-color: var(--color-button-primary-bg);
  color: var(--color-fg-on-emphasis);

  &:hover {
    background-color: var(--color-button-primary-hover-bg);
  }
}

.title {
  font-size: var(--font-size-base);
  line-height: var(--font-size-base-line-height);
  font-weight: var(--font-weight-semibold);
}

.status {
  margin-top: var(--space-4);
}

.status-value {
  font-weight: var(--font-weight-medium);
}

.applicants {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-12);
}

// Same card as DebtHeader's borrower boxes — they hold the same information.
.applicant-box {
  display: flex;
  width: 340px;
  max-width: 100%;
  flex-direction: column;
  gap: var(--space-8);
  padding: var(--space-12) var(--space-16);
  border: 1px solid var(--color-border-muted);
  border-radius: var(--radius-md);
  background-color: var(--color-canvas-default);
  color: inherit;
  text-decoration: none;

  &:hover {
    border-color: var(--color-border-hover);
  }
}

.applicant-role {
  font-size: var(--font-size-xs);
  line-height: var(--font-size-xs-line-height);
  font-weight: var(--font-weight-medium);
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--color-fg-subtle);
}

.applicant-identity {
  min-width: 0;
}

.applicant-name {
  font-weight: var(--font-weight-semibold);
}

.applicant-contact {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: var(--space-4);
}

.contact-line {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: var(--space-6);
}

.contact-icon {
  flex-shrink: 0;
  color: var(--color-icon-default);
}

.contact-value {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
